import React, { useEffect, useCallback, useState } from 'react';
import { useTranslator } from '@jutro/locale';
import _ from 'lodash';
import moment from 'moment';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import PropTypes from 'prop-types';
import { MLAgentPolicySummaryHeaderComponent } from 'gw-capability-quoteandbind-common-react';
import { MLReadOnlyCoverageComponent, MLHOEditScheduledPropertyComponent } from 'ml-components-common-react';
import HOPolicySummaryAdditionalDetailsComponent from '../HOPolicySummaryAdditionalDetailsComponent/HOPolicySummaryAdditionalDetailsComponent';
import SpecialLimitationCoverageTermsComponent from '../SpecialLimitationCoverageTermsComponent/SpecialLimitationCoverageTermsComponent';
import metadata from './HOPolicySummaryComponent.metadata.json5';
import messages from './HOPolicySummaryComponent.messages';
import styles from './HOPolicySummaryComponent.module.scss';

const specialLimitationsId = 'ztsg0okd8lvaha25n1nr1cmt9n9';

function HOPolicySummaryComponent(props) {
    const translator = useTranslator();
    const {
        value: policyDetails,
        startPrint,
        updateStartPrint,
        editCoverages,
        editPayment,
        editEffectiveDate,
        cancelChanges,
        makeMoreChanges,
        submitChanges,
        currentSteps,
        currentStepIndex,
        goToStep,
        selectablePath,
        detailsLoaded,
        ampMode,
        isPolicyChange,
        qbMode,
        gpaMode,
        isModernProduct
    } = props;

    const quoteId = _.get(policyDetails, 'quoteId.value', '');
    const coverages = _.get(policyDetails, 'coverageLobs.homeowners');   
   const occupancyType = qbMode ? "" : policyDetails.lobsPolChange.homeowners?.preQualQuestionSets?.value?.[0]?.answers?.HOPPreQualHomeCondoDwellingOccupancy_Ext
   const isOccupancySecondaryOrSeasonal = isModernProduct && ['HOPPreQualHomeCondoOccupancySecondary', 'HOPPreQualHomeCondoOccupancySeasonal'].includes(occupancyType);
   const selectedBaseCoverages = _.get(coverages, 'baseCoverages.value');
    const selectedOptionalCoverages = _.get(coverages, 'optionalCoverages_ML.value', []).filter((cov) => cov.selected);
    const selectedExclusionCoverages = _.get(coverages, 'exclusionCoverages_ML.value', []).filter((cov) => cov.selected);
    const specialLimitations = _.get(policyDetails, 'conditionCoverages_ML.value', []).filter((cov) => cov.publicID_ML === specialLimitationsId);
    const visitedSteps = currentSteps ? currentSteps.slice(0, currentStepIndex) : undefined;
    const [policyChangeHistories, setPolicyChangeHistories] = useState([]);
    const selectedVpp = _.get(coverages, 'valuablePersonalProperties_ML.value', []);
    const selectedSpp = _.get(coverages, 'schedulePersonalProperties_ML.value', []);
    const deedInfo_Ext = _.get(policyDetails, 'deedInfo_Ext.value', false);
    const dwellingDeedInfo_Ext = _.get(policyDetails, 'dwellingDeedInfo_Ext.value', '');

    useEffect(() => {
        selectedOptionalCoverages.forEach((cov) => {
            if (cov.code_ML === 'HOPScheduledPersonalProperty') {
                cov.scheduledItems = _.get(coverages, 'schedulePersonalProperties_ML.value', []);
            } else if (cov.code_ML === 'HOPValuablePersonalProperty') {
                cov.scheduledItems = _.get(coverages, 'valuablePersonalProperties_ML.value', []);
            }
        });
    }, [selectedOptionalCoverages, coverages]);

    const resolvers = {
        resolveComponentMap: {
            readOnlyCoverageComponent: MLReadOnlyCoverageComponent,
            specialLimitationCoverageTermsComponent: SpecialLimitationCoverageTermsComponent,
            hoPolicySummaryAdditionalDetailsComponent: HOPolicySummaryAdditionalDetailsComponent,
            MLAgentPolicySummaryHeaderComponent: MLAgentPolicySummaryHeaderComponent,
            MLHOEditScheduledPropertyComponent: MLHOEditScheduledPropertyComponent
        },
        resolveClassNameMap: styles
    };

    useEffect(() => {
        // get all steps, and then filter by the current steps
        if(ampMode && isPolicyChange && visitedSteps){
            const selectableSteps = visitedSteps.filter((policyChangeHistory) => {
                if(policyChangeHistory.path !== '/additionalchanges'){
                    return selectablePath[policyChangeHistory.path];
                }
                return null
            })
            setPolicyChangeHistories(selectableSteps)
        }
    },[])

    const generatePolicyChangeHistoriesOverrides = () => {
        const policyChangeHistoriesOverrides = policyChangeHistories.map((policyChangeHistory, index) => {
            return {
                [`policyChangeHistoryDiv${index}`]: {
                    className: (policyChangeHistories.length - 1) === index ? 'vertical-spacing-40' : 'vertical-spacing-20'
                },
                [`endorsementChangeName${index}`]: {
                    content: translator(selectablePath[policyChangeHistory.path])
                },
                [`endorsementTextLink${index}`]: {
                    onClick: () => goToStep(policyChangeHistory.path)
                }

            }
        })

        return Object.assign({}, ...policyChangeHistoriesOverrides);
    }

    const renderPolicyHeader = useCallback(() => {
        let headerMessage;
        if(ampMode && isPolicyChange) {
            headerMessage = translator(messages.policyX, { policyId: (_.get(policyDetails, 'policyId.value', ''))})
        }
        if(qbMode) {
            headerMessage = translator(messages.quoteX, { quoteId: (quoteId) })
        }
        return headerMessage;
    },[ampMode, isPolicyChange, policyDetails, qbMode, quoteId, translator])

    const generateSpecialLimitationsOverrides = () => {
        const overrides = specialLimitations.map((limitation, index) => {
            return {
                [`specialLimitationHeader${index}`]: {
                    content: limitation.name
                },
                [`specialLimitationDescription${index}`]: {
                    content: `${translator(messages.specialLimitationDescription)} ${gpaMode ? translator(messages.specialLimitationDescriptionGpa) : ''}`
                },
                [`specialLimitationCoverageTermsComponent${index}`]: {
                    model: limitation.allCoverageTerms_ML,
                    covIndex: index
                }
            };
        });

        return Object.assign({}, ...overrides);
    };

    const generateDiscountsOverrides = () => {
        const overrides = _.get(policyDetails, 'discounts.value', []).map((discount, index) => {
            return {
                [`discountName${index}`]: {
                    content: discount.name
                }
            };
        });

        return Object.assign({}, ...overrides);
    };

    const convertLocalDateToDate = useCallback((localDate) => {
        const parsedDate = new Date(localDate.year, localDate.month, localDate.day);
        return moment(parsedDate).format('MM/DD/YYYY');
    }, []);

    const getEntityName = () => {
        if (deedInfo_Ext === true) {
            return _.startCase(_.lowerCase(policyDetails?.dwellingDeedInfo_Ext?.value));
        }
        return '';
    }

    const getPrimaryLabel = () => {
        return deedInfo_Ext ? translator(messages.firstPersonInControl) : ''
    }

    const getLabelForSni = () => {
        return deedInfo_Ext ? translator(messages.secondPersonInControl) : translator(messages.coInsured)
    }

    const overrideProps = {
        policyHeader: {
            content: renderPolicyHeader(),
            visible : !gpaMode && (ampMode || qbMode)
        },
        agentPolicySummaryComponent: {
            visible: gpaMode,
            model: policyDetails
        },
        policySummaryInsuredInfoComponent: {
            model: policyDetails,
            visible : !gpaMode
        },
        hoPolicySummaryAdditionalDetailsComponent: {
            model: policyDetails,
            visible : !gpaMode,
            ampMode: ampMode,
            isPolicyChange: isPolicyChange
        },
        ampChangeEffectiveDateContainer: {
            visible: ampMode && isPolicyChange
        },
        changeEffectiveDateSectionHeader: {
            content: translator(messages.changeEffectiveDate, {date: convertLocalDateToDate(_.get(policyDetails, 'effective_ML.value', ''))})
        },
        policyChangeHistoryIterable: {
            data: policyChangeHistories
        },
        policySummaryConfirmationBoxComponent: {
            visible: qbMode || (isPolicyChange && ampMode),
            ampMode,
            qbMode,
            model: policyDetails,
            startPrint,
            updateStartPrint,
            editPayment,
            editEffectiveDate,
            cancelChanges,
            makeMoreChanges,
            submitChanges,
            detailsLoaded
        },
        includedFeaturesComponent: {
            model: _.get(policyDetails, 'coverageLobs.homeowners'),
            isPolicyChange: isPolicyChange && ampMode
        },
        editCoverageLink: {
            visible: isPolicyChange && ampMode,
            content: translator(messages.editCoverages),
            onClick: editCoverages
        },
        baseCoverages: {
            coverages: selectedBaseCoverages,
            displayHelpMessage: true,
            editCoverage: editCoverages,
            showEditCoverage: false,
            isOccupancySecondaryOrSeasonal,
            isModernProduct
        },
        baseCoveragesTotalValue: {
            showFractions: true,
            tag: 'span',
            currency: _.get(coverages, 'fullTermCost_Ext.currency.value.code'),
            value: _.get(coverages, 'fullTermCost_Ext.amount.value'),
            suffix: ' '.concat(translator(messages.perYear))
        },
        ampEditOptionalCoveragesLink: {
            visible: isPolicyChange && ampMode,
            content: translator(messages.editCoverages),
            onClick: editCoverages
        },
        optionalCoverages: {
            coverages: selectedOptionalCoverages,
            displayHelpMessage: true,
            editCoverage: editCoverages,
            showEditCoverage: false
        },
        vppCoverageContainer: {
            visible: selectedVpp.length > 0
        },
        vppCoverage: {
            properties: selectedVpp,
            isAgent: gpaMode
        },
        sppCoverageContainer: {
            visible: selectedSpp.length > 0
        },
        sppCoverage: {
            properties: selectedSpp,
            isAgent: gpaMode
        },
        exclusionsContainer: {
            visible: selectedExclusionCoverages.length > 0
        },
        exclusionCoverages: {
            coverages: selectedExclusionCoverages,
            displayHelpMessage: true,
            showEditCoverage: isPolicyChange && ampMode,
            editCoverage: editCoverages
        },
        specialLimitationsIterable: {
            data: specialLimitations
        },
        policyCoveragesHeader : {
            content : gpaMode ? translator(messages.coverages) : translator(messages.policyCoverages)
        },
        agentPolicySummaryInsuredInfo: {
            visible: gpaMode,
            content:
                (
                    <div className='row'>
                        <div className='ml-policy-summary-insured-info-gpa col-md-4 vertical-spacing-20'>
                            <span className='eyebrow-header'>
                                {translator(messages.insured)}
                            </span>
                            <ul className='styled-ul-list-body-copy'>
                                {(deedInfo_Ext) &&
                                    <li className='font-weight-bold'>
                                        {getEntityName()}
                                    </li>
                                }
                                {(deedInfo_Ext) &&
                                    <li>
                                        <span className='eyebrow-header'>
                                            {getPrimaryLabel()}
                                        </span>
                                    </li>
                                }
                                <li className='font-weight-bold'>
                                    {_.startCase(_.lowerCase(policyDetails.policyHolder.displayName.value))}
                                </li>
                                {(policyDetails.mailingAddress.addressLine1.value) &&
                                    <li>{policyDetails.mailingAddress.addressLine1.value}</li>
                                }

                                {(policyDetails.mailingAddress.addressLine2.value) && <li> {policyDetails.mailingAddress.addressLine2.value}    </li>

                                }

                                {(policyDetails.mailingAddress.addressLine3.value) && <li> {policyDetails.mailingAddress.addressLine3.value} </li>

                                }
                                <li>
                                    {policyDetails.mailingAddress.city.value}, {policyDetails.mailingAddress.state.value.code}  {policyDetails.mailingAddress.postalCode.value}
                                </li>
                            </ul>
                        </div>
                        {(policyDetails.secondaryInsured_ML.value) &&
                            <div classssName='ml-policy-summary-insured-info-gpa col-md-4 vertical-spacing-20'>

                                <span className='eyebrow-header'>
                                    {getLabelForSni()}
                                </span>
                                <ul className='styled-ul-list-body-copy'>
                                    <li className='font-weight-bold'>
                                        {policyDetails.secondaryInsured_ML.displayName.value}
                                    </li>
                                    {(policyDetails.secondaryInsured_ML.address.addressLine1.value) &&
                                        <li>{policyDetails.secondaryInsured_ML.address.addressLine1.value}</li>
                                    }

                                    {(policyDetails.secondaryInsured_ML.address.addressLine2.value) && <li> {policyDetails.secondaryInsured_ML.address.addressLine2.value}    </li>

                                    }

                                    {(policyDetails.secondaryInsured_ML.address.addressLine3.value) && <li> {policyDetails.secondaryInsured_ML.address.addressLine3.value} </li>

                                    }
                                    <li>
                                        {policyDetails.secondaryInsured_ML.address.city.value}, {policyDetails.secondaryInsured_ML.address.state.value.code}  {policyDetails.secondaryInsured_ML.address.postalCode.value}
                                    </li>
                                </ul>

                            </div>
                        }

                    </div>
                )


        },
        ampInsuranceCoverageText: {
            visible: ampMode
        },
        ampPolicySummaryConfirmationBoxComponent: {
            visible: isPolicyChange && ampMode,
            ampMode: ampMode,
            model: policyDetails,
            startPrint,
            updateStartPrint,
            editPayment,
            editEffectiveDate,
            cancelChanges,
            makeMoreChanges,
            submitChanges,
            detailsLoaded
        },
        billingSummary: {
            visible: gpaMode,
            content: (
                <div className='vertical-spacing-40'>
                    <h2 className='section-header vertical-spacing-20'>
                        { translator(messages.billing) }
                    </h2>
                    <hr />
                    <p><strong>{policyDetails.paymentDetails.paymentMethod.value}</strong></p>
                    <hr />
                </div>
            )

        },
        discountsContainer :{
            visible : gpaMode && policyDetails.discounts.value && policyDetails.discounts.length  > 0
        },
        discountIterable : {
            data : policyDetails.discounts.value
        },
        ...generatePolicyChangeHistoriesOverrides(),
        ...generateSpecialLimitationsOverrides(),
        ...generateDiscountsOverrides()
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={policyDetails}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

HOPolicySummaryComponent.propTypes = {
    value: PropTypes.shape({}).isRequired,
    startPrint: PropTypes.bool,
    updateStartPrint: PropTypes.func,
    editCoverages: PropTypes.func.isRequired,
    editPayment: PropTypes.func.isRequired,
    gpaMode: PropTypes.bool,
    qbMode: PropTypes.bool,
    ampMode: PropTypes.bool,
    isPolicyChange: PropTypes.bool
};

HOPolicySummaryComponent.defaultProps = {
    startPrint: false,
    updateStartPrint: _.noop,
    gpaMode : false,
    qbMode: false,
    ampMode: false,
    isPolicyChange: false
};

export default HOPolicySummaryComponent;
